import { type LoaderActivity } from '@littlebird/ui-utils';
import { animate, useReducedMotion } from 'motion/react';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from 'react';

import { FINE_POINTER_QUERY } from '@/assets/icons/logo-motion';

import {
  createAnimationScope,
  ignoreCancellation,
  type AnimationScope,
} from './animation-scope';
import {
  createLogoActs,
  createLogoMotionValues,
  playNotice,
  playStartle,
  resetPose,
  type LogoAct,
  type LogoMotionValues,
} from './logo-acts';

const POINTER_LOOK_RADIUS_PX = 64;
const POINTER_LOOK_COOLDOWN_MS = 4000;
const POKE_COOLDOWN_MS = 1500;

type ActWeights = Partial<Record<LogoAct, number>>;

const PERCHED_WEIGHTS: ActWeights = {
  stillness: 3,
  ponder: 1.5,
  crouch: 1,
  preen: 1,
  hopTurn: 1,
  sip: 1,
  stretch: 1,
};
const FLIGHT_WEIGHTS: ActWeights = { flutter: 2, dart: 1, glide: 1 };
const ALL_ACTS = Object.keys({
  ...PERCHED_WEIGHTS,
  ...FLIGHT_WEIGHTS,
}) as LogoAct[];

const ACTIVITY_BIAS: Record<LoaderActivity, ActWeights> = {
  think: { ponder: 3, stillness: 1.2, crouch: 0.6, hopTurn: 0.5 },
  search: { hopTurn: 2.2, stretch: 1.4, stillness: 0.7 },
  read: { sip: 2.5, stillness: 0.8 },
  write: { crouch: 2.5, dart: 2, stillness: 0.7 },
};
const ACTIVITY_SIGNATURE: Record<LoaderActivity, LogoAct> = {
  think: 'ponder',
  search: 'hopTurn',
  read: 'sip',
  write: 'crouch',
};

export const useLogoMotionValues = () => {
  const [values] = useState(createLogoMotionValues);
  return values;
};

export type LogoLoaderHandle = {
  values: LogoMotionValues;
  containerRef: RefObject<HTMLDivElement | null>;
  poke: () => void;
};

type UseLogoLoaderOptions = {
  activity?: LoaderActivity;
  isExiting?: boolean;
  active?: boolean;
  calm?: boolean;
  flightOnly?: boolean;
  onExitComplete?: () => void;
};

export const useLogoLoader = ({
  activity,
  isExiting = false,
  active = true,
  calm = false,
  flightOnly = false,
  onExitComplete,
}: UseLogoLoaderOptions = {}): LogoLoaderHandle => {
  const values = useLogoMotionValues();
  const reduceMotion = useReducedMotion();

  const containerRef = useRef<HTMLDivElement>(null);
  const activityRef = useRef<LoaderActivity>('think');
  const pendingReactionRef = useRef<LogoAct | null>(null);
  const calmRef = useRef(calm);
  const onExitCompleteRef = useRef(onExitComplete);
  const restingRef = useRef(false);
  const lastPokeAtRef = useRef(0);
  const pokeScopeRef = useRef<AnimationScope | null>(null);

  useEffect(() => {
    if (!activity || activity === activityRef.current) return;
    activityRef.current = activity;
    pendingReactionRef.current = ACTIVITY_SIGNATURE[activity];
  }, [activity]);

  useEffect(() => {
    calmRef.current = calm;
    onExitCompleteRef.current = onExitComplete;
  }, [calm, onExitComplete]);

  useEffect(() => {
    if (!active) return;
    if (reduceMotion) {
      if (isExiting) {
        const fade = animate(values.opacity, 0, {
          duration: 0.2,
          ease: 'easeOut',
        });
        void fade.finished.then(() => onExitCompleteRef.current?.());
        return () => fade.stop();
      }
      const pulse = animate(values.opacity, [1, 0.55, 1], {
        duration: 2.4,
        repeat: Infinity,
        ease: 'easeInOut',
      });
      return () => pulse.stop();
    }

    const scope = createAnimationScope();

    if (isExiting) {
      const { exitFlight } = createLogoActs(values, scope);
      exitFlight()
        .then(() => onExitCompleteRef.current?.())
        .catch(ignoreCancellation);
      return () => scope.cancel();
    }

    const rest = (ms: number) => {
      restingRef.current = true;
      return scope.rest(ms).finally(() => {
        restingRef.current = false;
      });
    };
    const restBetween = () => rest(400 + Math.random() * 500);
    const { acts, land, takeoff } = createLogoActs(values, {
      track: scope.track,
      rest,
    });

    const personality = Object.fromEntries(
      ALL_ACTS.map((act) => [act, 0.6 + Math.random()])
    ) as Record<LogoAct, number>;

    const pickAct = (weights: ActWeights, previous?: LogoAct) => {
      const bias = ACTIVITY_BIAS[activityRef.current];
      const options: [LogoAct, number][] = [];
      let total = 0;
      for (const [act, weight] of Object.entries(weights) as [
        LogoAct,
        number,
      ][]) {
        if (act === previous) continue;
        const weighted = weight * personality[act] * (bias[act] ?? 1);
        options.push([act, weighted]);
        total += weighted;
      }
      let roll = Math.random() * total;
      for (const [act, weighted] of options) {
        roll -= weighted;
        if (roll <= 0) return act;
      }
      return options[options.length - 1][0];
    };

    const takePendingReaction = () => {
      const reaction = pendingReactionRef.current;
      pendingReactionRef.current = null;
      return reaction;
    };

    const settleIn = () => {
      if (values.opacity.get() === 0) {
        resetPose(values, 0);
        return;
      }
      const neutralTargets = [
        [values.x, 0],
        [values.y, 0],
        [values.rotate, 0],
        [values.scaleX, 1],
        [values.scaleY, 1],
        [values.opacity, 1],
        [values.eyeGaze, 0],
      ] as const;
      for (const [value, target] of neutralTargets) {
        if (value.get() !== target) {
          scope.track(
            animate(value, target, { duration: 0.2, ease: 'easeOut' })
          );
        }
      }
    };

    const run = async () => {
      settleIn();
      let previous: LogoAct | undefined;
      await acts.flutter();
      if (!flightOnly) await land();
      while (true) {
        if (flightOnly) {
          previous = pickAct(FLIGHT_WEIGHTS, previous);
          await acts[previous]();
          await rest(250 + Math.random() * 400);
          continue;
        }
        const perchedCount = 1 + Math.floor(Math.random() * 2);
        for (let i = 0; i < perchedCount; i++) {
          await restBetween();
          previous =
            takePendingReaction() ?? pickAct(PERCHED_WEIGHTS, previous);
          await acts[previous]();
        }
        if (calmRef.current) continue;
        await restBetween();
        await takeoff();
        const flightCount = 1 + Math.floor(Math.random() * 2);
        for (let i = 0; i < flightCount; i++) {
          previous = pickAct(FLIGHT_WEIGHTS, previous);
          await acts[previous]();
          await rest(250 + Math.random() * 400);
        }
        await land();
      }
    };

    run().catch(ignoreCancellation);
    return () => scope.cancel();
  }, [reduceMotion, isExiting, active, values, flightOnly]);

  useEffect(() => {
    if (reduceMotion || !window.matchMedia(FINE_POINTER_QUERY).matches) return;
    let lookScope: AnimationScope | null = null;
    let lastLookAt = 0;
    let frameQueued = false;
    let lastPointerEvent: PointerEvent | null = null;

    const onPointerMove = (event: PointerEvent) => {
      lastPointerEvent = event;
      if (frameQueued) return;
      frameQueued = true;
      requestAnimationFrame(() => {
        frameQueued = false;
        const element = containerRef.current;
        const pointer = lastPointerEvent;
        if (!element || !pointer) return;
        const now = performance.now();
        if (now - lastLookAt < POINTER_LOOK_COOLDOWN_MS) return;
        if (!restingRef.current) return;
        const rect = element.getBoundingClientRect();
        const dx = pointer.clientX - (rect.left + rect.width / 2);
        const dy = pointer.clientY - (rect.top + rect.height / 2);
        const distance = Math.hypot(dx, dy);
        if (distance > POINTER_LOOK_RADIUS_PX || distance < 6) return;
        lastLookAt = now;
        lookScope?.cancel();
        lookScope = createAnimationScope();
        playNotice(values, dy < 0 ? -8 : 6).forEach(lookScope.track);
      });
    };

    window.addEventListener('pointermove', onPointerMove, { passive: true });
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      lookScope?.cancel();
    };
  }, [reduceMotion, values]);

  const poke = useCallback(() => {
    if (reduceMotion || !restingRef.current) return;
    const now = performance.now();
    if (now - lastPokeAtRef.current < POKE_COOLDOWN_MS) return;
    lastPokeAtRef.current = now;
    pokeScopeRef.current?.cancel();
    const scope = createAnimationScope();
    playStartle(values).forEach(scope.track);
    pokeScopeRef.current = scope;
  }, [reduceMotion, values]);

  useEffect(() => () => pokeScopeRef.current?.cancel(), []);

  return { values, containerRef, poke };
};
